import { object } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { Formik, isString } from 'formik';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { usePageTitle, usePersistedSort, usePersistedState } from '../Lib';

import { ApiCreateOrgProduct, ApiOrg, ApiOrgProduct } from '@kaiizen/kai-pro-api';
import { useAllProductSupplierOrgs, useAuth } from '@kaiizen/kai-pro-api-react-state';

import { formatUsd } from '../Presenters';
import { LAYOUT2 } from '../Styles';

import { useOrgProductsWithFilter, useToast } from '../State';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { PageLayout } from '../Layout';
import { getOrgProductProductSupplierDisplayName } from '../Lib';
import { NoRecordsPlaceholder } from '../Partials';
import { Button, DataTable, FlyOut, FormFieldsRequirementNote, FormikLabeledInput, LabeledInput, StyledForm } from '../Widgets';
import { GenericLoadingPage } from './GenericPages';

const SKU_TEXT_STYLE = css`
  label: SKU_TEXT_STYLE;
  white-space: nowrap;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const subHeaderButtonsStyle = css`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const productImageStyle = css`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: ${LAYOUT2.spacingHalf}px;
`;

const truncateDescription = (description: string) => {
  return description.length > 70 ? `${description.substring(0, 70).trim()}...` : description;
};

const ProductFilter = memo(({ filterText, setFilterText }: { filterText: string; setFilterText: (text: string) => void }) => (
  <LabeledInput
    label='Filter Products'
    value={filterText}
    placeholder='Search by SKU, title, brand...'
    onUpdate={setFilterText}
    size='small'
    width='400px'
  />
));

export const OrgProductsPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get('archived') === 'true';
  usePageTitle('Products');
  const [filterText, setFilterText] = usePersistedState('products:filter', '');
  const { order: sortOrder, orderBy: sortOrderBy, onSortChange } = usePersistedSort('products:sort', 'desc', 'updatedAt');
  const { orgProducts, orgProductMutators, isLoading: orgProductsLoading } = useOrgProductsWithFilter({
    archived: showArchived,
    queryString: filterText || undefined,
  });
  const { productSupplierOrgs, productSuppliersLoading } = useAllProductSupplierOrgs();
  const { showToast, showErrorToast } = useToast();
  const { userCan } = useAuth();
  const canCreateProduct = userCan('create', 'OrgProduct');
  const canUpdateProduct = userCan('update', 'OrgProduct');
  const navigate = useNavigate();
  const hasLoadedInitialData = useRef(false);

  useEffect(() => {
    if (orgProducts && productSupplierOrgs) {
      hasLoadedInitialData.current = true;
    }
  }, [orgProducts, productSupplierOrgs]);

  const productSupplierOrgsById = useMemo(() =>
    object(productSupplierOrgs, {
      withKey: ({ id }) => id,
      with: (fields: ApiOrg, key: string) => ({ ...fields, key }),
    }), [productSupplierOrgs]);

  const orgProductToEdit = id ? orgProducts?.find(orgProduct => orgProduct.id === id) : null;
  const flyoutOpen = id === 'new' || !!orgProductToEdit;

  const displayOrgProducts = useMemo(() =>
    orgProducts
      ?.map((orgProduct: ApiOrgProduct) => {
        const supplier = productSupplierOrgsById[orgProduct.productSupplierOrgId!];
        return ({
          id: orgProduct.id,
          link: `/product/${orgProduct.id}`,
          sku: orgProduct.thdStoreSku || orgProduct.sku,
          title: orgProduct.title ? <div title={orgProduct.title}>{truncateDescription(orgProduct.title)}</div> : '-',
          supplier: getOrgProductProductSupplierDisplayName(orgProduct, productSupplierOrgsById),
          price: orgProduct.price,
          description: orgProduct.description ? <div title={orgProduct.description}>{truncateDescription(orgProduct.description)}</div> : '-',
          brand: orgProduct.brand || '-',
          image: orgProduct.image,
          createdAt: orgProduct.createdAt,
          updatedAt: orgProduct.updatedAt,
          color: orgProduct.color || '-',
          menu: [
            {
              label: canUpdateProduct && supplier?.key !== 'homeDepot' ? 'Edit Product Details' : 'View Product Details',
              action: () => {
                navigate(`/product/${orgProduct.id}`);
              },
            },
            ...(canUpdateProduct && supplier?.key !== 'homeDepot'
              ? [{
                label: orgProduct.archivedAt ? 'Unarchive' : 'Archive',
                action: async () => {
                  const isArchiving = !orgProduct.archivedAt;
                  await orgProductMutators.update(orgProduct.id, {
                    archivedAt: orgProduct.archivedAt ? undefined : new Date().toISOString(),
                  } as any);
                  trackEvent(isArchiving ? ANALYTICS_EVENTS.productArchived : ANALYTICS_EVENTS.productUnarchived, {
                    productId: orgProduct.id,
                    title: orgProduct.title,
                    sku: orgProduct.sku,
                  });
                },
              }]
              : []),
          ],
        });
      }), [orgProducts, productSupplierOrgsById, orgProductMutators, navigate, canUpdateProduct]);

  // Show loading page only on initial load before any data has been loaded
  if (!hasLoadedInitialData.current && (orgProductsLoading || productSuppliersLoading)) {
    return <GenericLoadingPage title='Products' />;
  }

  const addOrgProduct = async (values: ApiCreateOrgProduct) => {
    const newProduct = await orgProductMutators.create(values);
    trackEvent(ANALYTICS_EVENTS.productCreated, {
      productId: newProduct.id,
      title: newProduct.title,
      sku: newProduct.sku,
      brand: newProduct.brand,
      price: newProduct.price,
    });
    navigate('/products');
  };

  const updateOrgProduct = async (
    orgProductId: string,
    values: { title: string; description: string; price: number | string; brand: string; image: string },
  ) => {
    const priceValue = values.price === '' ? undefined : Number(values.price);
    await orgProductMutators.update(orgProductId, { ...values, price: priceValue });
    trackEvent(ANALYTICS_EVENTS.productUpdated, {
      productId: orgProductId,
      title: values.title,
      brand: values.brand,
      price: priceValue,
    });
    navigate('/products');
  };

  const haveOrgProducts = displayOrgProducts && displayOrgProducts.length > 0;

  return (
    <PageLayout
      title={'Products' + (showArchived ? ' (archived)' : '')}
      contentLayoutStyling={'standard'}
      subheader={<ProductFilter filterText={filterText} setFilterText={setFilterText} />}
      subHeaderButtons={
        <div css={subHeaderButtonsStyle}>
          <Button
            color={showArchived ? 'gray' : 'bluePrimary'}
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              if (showArchived) {
                newParams.delete('archived');
              } else {
                newParams.set('archived', 'true');
              }
              setSearchParams(newParams);
            }}
          >
            {showArchived ? 'Show Active' : 'Show Archived'}
          </Button>
          {canCreateProduct && (
            <Button
              color='bluePrimary'
              onClick={() => {
                navigate('/product/new');
              }}
            >
              New Product
            </Button>
          )}
        </div>
      }
    >
      {!haveOrgProducts
        ? (
          <NoRecordsPlaceholder
            recordType='Products'
            qualifier={showArchived ? 'Archived' : undefined}
            description={canCreateProduct ? 'Create your first product to get started.' : 'No products have been created yet.'}
            createButtonText={canCreateProduct ? 'Add a Product' : undefined}
            createFirstRecord={canCreateProduct ? () => navigate('/product/new') : undefined}
            iconType='products'
          />
        )
        : (
          <DataTable
            recordType='product'
            items={displayOrgProducts}
            defaultOrder={sortOrder}
            defaultOrderBy={sortOrderBy}
            onSortChange={onSortChange}
            columns={[
              { name: 'sku', sortable: true, width: '150px', format: (value: string) => <div title={value} css={SKU_TEXT_STYLE}>{value || '-'}</div> },
              { name: 'supplier', sortable: true, width: '100px' },
              { name: 'brand', sortable: true, width: '100px' },
              { name: 'price', sortable: true, width: '100px', format: (value: number) => formatUsd(value) },
              {
                name: 'image',
                sortable: true,
                width: '80px',
                sortMethod: (a: any, b: any) => isString(a) === isString(b) ? 0 : isString(a) ? 1 : -1,
                format: (value: string) => value ? <img src={value} css={productImageStyle} /> : '-',
              },
              { name: 'title', sortable: true, width: '300px' },
              { name: 'color', sortable: true, width: '100px' },
              { name: 'description', sortable: false },
            ]}
            showPagination={true}
            pageSize={25}
          />
        )}
      <FlyOut
        open={flyoutOpen}
        flyOutClosed={() => navigate('/products')}
        title={orgProductToEdit ? (canUpdateProduct ? 'Edit Product Details' : 'Product Details') : 'Add a Product'}
        subheader={canUpdateProduct || !orgProductToEdit ? <FormFieldsRequirementNote variant='optional' /> : undefined}
      >
        <Formik<{ sku: string; title: string; description: string; price: number | string; brand: string; image: string; color: string }>
          initialValues={{
            sku: orgProductToEdit?.sku || '',
            title: orgProductToEdit?.title || '',
            description: orgProductToEdit?.description || '',
            price: orgProductToEdit?.price || '',
            brand: orgProductToEdit?.brand || '',
            image: orgProductToEdit?.image || '',
            color: orgProductToEdit?.color || '',
          }}
          enableReinitialize
          onSubmit={async values => {
            try {
              if (orgProductToEdit) {
                const isHomeDepotProduct = !!(productSupplierOrgsById[orgProductToEdit.productSupplierOrgId!]?.key === 'homeDepot');
                if (isHomeDepotProduct) {
                  return;
                }
                await updateOrgProduct(orgProductToEdit.id, values);
                showToast('Product updated successfully', 'success');
              } else {
                const priceValue = values.price === '' ? undefined : Number(values.price);
                await addOrgProduct({ ...values, price: priceValue });
                showToast('Product created successfully', 'success');
              }
            } catch (error) {
              showErrorToast(error, orgProductToEdit ? 'update a product' : 'create a product');
            }
          }}
        >
          {({ handleSubmit, values }) => {
            const isHomeDepotProduct = !!(orgProductToEdit && productSupplierOrgsById[orgProductToEdit.productSupplierOrgId!]?.key === 'homeDepot');
            const readOnly = isHomeDepotProduct || !canUpdateProduct;
            return (
              <StyledForm onSubmit={handleSubmit}>
                <FormikLabeledInput
                  name='sku'
                  label='SKU'
                  placeholder='Enter product SKU'
                  disabled={readOnly}
                  required
                />
                <FormikLabeledInput
                  name='title'
                  label='Title'
                  placeholder='Enter product title'
                  disabled={readOnly}
                  required
                />
                <FormikLabeledInput
                  name='brand'
                  label='Brand'
                  placeholder='Enter product brand'
                  disabled={readOnly}
                  optional
                />
                <FormikLabeledInput
                  name='image'
                  label='Image URL'
                  placeholder='Enter product image URL'
                  disabled={readOnly}
                  optional
                />
                <FormikLabeledInput
                  name='description'
                  label='Description'
                  placeholder='Enter product description'
                  multiline
                  rows={3}
                  disabled={readOnly}
                  optional
                />
                <FormikLabeledInput
                  name='price'
                  label='Price'
                  placeholder='Enter price'
                  type='number'
                  disabled={readOnly}
                  optional
                />
                <FormikLabeledInput
                  name='color'
                  label='Color'
                  placeholder='Enter product color'
                  disabled={readOnly}
                  optional
                />
                {!readOnly && (
                  <Button
                    color='green'
                    disabled={!values.title || !values.sku}
                    type='submit'
                  >
                    {orgProductToEdit ? 'Update Product' : 'Add Product'}
                  </Button>
                )}
              </StyledForm>
            );
          }}
        </Formik>
      </FlyOut>
    </PageLayout>
  );
};
